import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    TextField,
    Button,
    Alert,
    CircularProgress,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip
} from '@mui/material';
import { ArrowBack, Create, Send, Support } from '@mui/icons-material';
import api from '../axios/axiosInstance';

const CreateTicket = ({ onBack }) => {
    const [ticket, setTicket] = useState({
        title: '',
        description: '',
        issue_type: '',
        priority: 'medium'
    });
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, type: '', message: '' });

    const issueTypes = [
        { value: 'Billing/Subscription', label: 'Billing/Subscription', color: '#d32f2f' },
        { value: 'Students', label: 'Students', color: '#1976d2' },
        { value: 'Assessment', label: 'Assessment', color: '#2e7d32' },
        { value: 'Teacher Resources', label: 'Teacher Resources', color: '#ed6c02' },
        { value: 'Subject', label: 'Subject', color: '#7b1fa2' },
        { value: 'Resources', label: 'Resources', color: '#0288d1' },
        { value: 'Other', label: 'Other', color: '#616161' }
    ];

    const priorities = [
        { value: 'low', label: 'Low', color: '#4caf50' },
        { value: 'medium', label: 'Medium', color: '#ff9800' },
        { value: 'high', label: 'High', color: '#f44336' },
        { value: 'urgent', label: 'Urgent', color: '#9c27b0' }
    ];

    const handleChange = (field, value) => {
        setTicket(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const showAlert = (type, message) => {
        setAlert({ show: true, type, message });
        setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!ticket.title.trim()) {
            showAlert('error', 'Please provide a ticket title');
            return;
        }
        
        if (!ticket.description.trim()) {
            showAlert('error', 'Please describe your issue');
            return;
        }
        
        if (!ticket.issue_type) {
            showAlert('error', 'Please select an issue type');
            return;
        }

        setLoading(true);
        
        try {
            const response = await api.post('/auth/tickets/', ticket);
            showAlert('success', `Ticket #${response.data.ticket_number} created successfully! We'll get back to you soon.`);
            
            // Clear form after successful submission
            setTimeout(() => {
                onBack();
            }, 3000);
            
        } catch (error) {
            showAlert('error', error.response?.data?.error || 'Failed to create ticket. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getPriorityColor = (priority) => {
        const found = priorities.find(p => p.value === priority);
        return found ? found.color : '#616161';
    };

    const getIssueTypeColor = (issueType) => {
        const found = issueTypes.find(t => t.value === issueType);
        return found ? found.color : '#616161';
    };

    return (
        <Box sx={{ p: 3, maxWidth: '800px', margin: '0 auto' }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBack />
                </IconButton>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Support sx={{ fontSize: 32, color: '#2e7d32', mr: 1 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Create Support Ticket
                    </Typography>
                </Box>
            </Box>

            {/* Alert */}
            {alert.show && (
                <Alert severity={alert.type} sx={{ mb: 3 }}>
                    {alert.message}
                </Alert>
            )}

            {/* Ticket Form */}
            <Card sx={{ borderRadius: '12px' }}>
                <CardContent sx={{ p: 4 }}>
                    <form onSubmit={handleSubmit}>
                        {/* Issue Type & Priority Row */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                            {/* Issue Type */}
                            <FormControl fullWidth>
                                <InputLabel>Issue Type *</InputLabel>
                                <Select
                                    value={ticket.issue_type}
                                    label="Issue Type *"
                                    onChange={(e) => handleChange('issue_type', e.target.value)}
                                >
                                    {issueTypes.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Chip
                                                    size="small"
                                                    label=""
                                                    sx={{ 
                                                        width: 12, 
                                                        height: 12, 
                                                        mr: 1,
                                                        bgcolor: type.color,
                                                        '& .MuiChip-label': { padding: 0 }
                                                    }}
                                                />
                                                {type.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Priority */}
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Priority</InputLabel>
                                <Select
                                    value={ticket.priority}
                                    label="Priority"
                                    onChange={(e) => handleChange('priority', e.target.value)}
                                >
                                    {priorities.map((priority) => (
                                        <MenuItem key={priority.value} value={priority.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Chip
                                                    size="small"
                                                    label=""
                                                    sx={{ 
                                                        width: 12, 
                                                        height: 12, 
                                                        mr: 1,
                                                        bgcolor: priority.color,
                                                        '& .MuiChip-label': { padding: 0 }
                                                    }}
                                                />
                                                {priority.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Title */}
                        <Box sx={{ mb: 3 }}>
                            <TextField
                                fullWidth
                                label="Ticket Title *"
                                variant="outlined"
                                placeholder="Brief summary of your issue"
                                value={ticket.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                inputProps={{ maxLength: 160 }}
                                helperText={`${ticket.title.length}/160 characters`}
                            />
                        </Box>

                        {/* Description */}
                        <Box sx={{ mb: 4 }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                label="Describe Your Issue *"
                                variant="outlined"
                                placeholder="Please provide detailed information about your issue. Include any error messages, steps you took, and what you expected to happen..."
                                value={ticket.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                helperText="The more details you provide, the better we can help you"
                            />
                        </Box>

                        {/* Preview */}
                        {(ticket.issue_type || ticket.priority !== 'medium') && (
                            <Box sx={{ mb: 3, p: 2, bgcolor: '#f8f9fa', borderRadius: '8px' }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Ticket Preview:
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {ticket.issue_type && (
                                        <Chip
                                            size="small"
                                            label={ticket.issue_type}
                                            sx={{ bgcolor: getIssueTypeColor(ticket.issue_type), color: 'white' }}
                                        />
                                    )}
                                    <Chip
                                        size="small"
                                        label={`Priority: ${ticket.priority}`}
                                        sx={{ bgcolor: getPriorityColor(ticket.priority), color: 'white' }}
                                    />
                                </Box>
                            </Box>
                        )}

                        {/* Submit Button */}
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                onClick={onBack}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                                sx={{ minWidth: 120 }}
                            >
                                {loading ? 'Creating...' : 'Create Ticket'}
                            </Button>
                        </Box>
                    </form>
                </CardContent>
            </Card>

            {/* Help Text */}
            <Box sx={{ mt: 3, p: 3, bgcolor: '#e3f2fd', borderRadius: '8px' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>💡 Tips for better support:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    • Be specific about what you were trying to do<br />
                    • Include any error messages you see<br />
                    • Mention which browser or device you're using<br />
                    • Attach screenshots if helpful (you can email them to support@gradely.ca)
                </Typography>
            </Box>
        </Box>
    );
};

export default CreateTicket;